import { createElement, forwardRef, memo } from 'react';
import defaultAttributes from './defaultAttributes';
import { IconNode, YCloudIconsProps } from './types';
import { mergeClasses, hasA11yProp } from '@ycloud-web/shared';
import { useYCloudContext } from './context';

interface IconComponentProps extends YCloudIconsProps {
  iconNode: IconNode;
}

/**
 * YCloud icon component
 *
 * @component Icon
 * @param {object} props
 * @param {string} props.color - The color of the icon
 * @param {number} props.size - The size of the icon
 * @param {number} props.strokeWidth - The stroke width of the icon
 * @param {boolean} props.absoluteStrokeWidth - Whether to use absolute stroke width
 * @param {string} props.className - The class name of the icon
 * @param {IconNode} props.children - The children of the icon
 * @param {IconNode} props.iconNode - The icon node of the icon
 *
 * @returns {ForwardRefExoticComponent} YCloudIcon
 */
const Icon = forwardRef<SVGSVGElement, IconComponentProps>(
  (
    { color, size, strokeWidth, absoluteStrokeWidth, className = '', children, iconNode, ...rest },
    ref,
  ) => {
    const {
      size: contextSize = 24,
      strokeWidth: contextStrokeWidth = 2,
      absoluteStrokeWidth: contextAbsoluteStrokeWidth = false,
      color: contextColor = 'currentColor',
      className: contextClass = '',
    } = useYCloudContext() ?? {};

    const calculatedStrokeWidth =
      (absoluteStrokeWidth ?? contextAbsoluteStrokeWidth)
        ? (Number(strokeWidth ?? contextStrokeWidth) * 24) / Number(size ?? contextSize)
        : (strokeWidth ?? contextStrokeWidth);

    return createElement(
      'svg',
      {
        ref,
        ...defaultAttributes,
        width: size ?? contextSize ?? defaultAttributes.width,
        height: size ?? contextSize ?? defaultAttributes.height,
        stroke: color ?? contextColor,
        strokeWidth: calculatedStrokeWidth,
        className: mergeClasses('ycloud', contextClass, className),
        ...(!children && !hasA11yProp(rest) && { 'aria-hidden': 'true' }),
        ...rest,
      },
      [
        ...iconNode.map(([tag, attrs]) => createElement(tag, attrs)),
        ...(Array.isArray(children) ? children : [children]),
      ],
    );
  },
);

// Memoize Icon component to prevent unnecessary re-renders
// Icons are typically pure and don't need to re-render unless props change
export default memo(Icon);
